// DevHub: the ONE grouped search-result list, shared by the home hero (HomeSearch) and the Cmd+K
// modal (SearchModal) so both surfaces look and behave identically — one coherent tool.
//
// Catalog entities render as standalone rows; techdocs hits are clustered under a header naming the
// owning doc (its `entityTitle`, e.g. "Azure DevOps") so a page result like "Home" always shows which
// doc it belongs to, and all hits from one doc sit together. Each navigable row is a <button> carrying
// `data-index` (its position in the flat results array) so either surface's arrow-key nav can target
// it — the hero drives `is-active` from the `activeIndex` prop; the modal toggles it via a DOM effect.
import { useMemo } from 'react';
import type { SearchResult } from '@backstage/plugin-search-common';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { searchMessages } from '../../i18n/search';
import {
  DOCS_TYPE,
  type ResultKind,
  classifyResult,
  docNameOf,
  isDocRoot,
  ResultSnippet,
} from './resultDisplay';
import './SearchResultGroups.css';

// Distinct ODX icon per kind (all registered in index.tsx).
const KIND_ICON: Record<ResultKind, string> = {
  entity: 'core::tile-menu',
  doc: 'core::document',
  page: 'core::document',
  section: 'core::bookmark',
};

export type GroupItem = { index: number; result: SearchResult };
export type ResultGroup =
  | { kind: 'single'; index: number; result: SearchResult }
  // A three-tier finding:
  //   • `name`    — TIER 0: the finding's title (a plain, non-clickable header).
  //   • `primary` — TIER 1: the catalog ENTITY and the DOC, as sibling rows. Either may be absent:
  //                 there is a DOC row only when the doc's own landing page actually matched.
  //   • `content` — TIER 2: the doc's pages/sections (the matching content within it).
  | { kind: 'group'; name: string; primary: GroupItem[]; content: GroupItem[] };

// The entity a result belongs to (kind:namespace/name), from a /docs/… or /catalog/… location. Unifies
// a catalog entity with its OWN techdocs so they cluster into one finding. Falls back to the doc name.
const ENTITY_FROM_LOCATION = /\/(?:docs|catalog)\/([^/]+)\/([^/]+)\/([^/?#]+)/i;
function groupKeyOf(result: SearchResult): string {
  const m = (result.document.location || '').match(ENTITY_FROM_LOCATION);
  if (m) return `${m[2].toLowerCase()}:${m[1].toLowerCase()}/${m[3].toLowerCase()}`;
  const docName = docNameOf(result);
  return docName ? `doc:${docName}` : `loc:${result.document.location || ''}`;
}

/**
 * Cluster a flat result list into three-tier findings by ENTITY. A catalog entity and its TechDocs
 * pages become ONE finding:
 *   TIER 0 (`name`)    — the finding's title, rendered as a plain header (not a result row).
 *   TIER 1 (`primary`) — the catalog ENTITY row and the DOC row, as siblings. The DOC row exists ONLY
 *                        when the doc's own landing page matched; a deeper page or a section is never
 *                        promoted into it (that is what made a row read as the doc while navigating to
 *                        an anchor inside it).
 *   TIER 2 (`content`) — the doc's matching pages/sections.
 * Entities with no docs render as a standalone row. `index` on every item is the position in the FLAT
 * input array, so keyboard nav (visualOrder / activeIndex) stays correct.
 */
export function buildGroups(results: SearchResult[]): ResultGroup[] {
  const byKey = new Map<string, GroupItem[]>();
  const keyOrder: string[] = [];
  results.forEach((result, index) => {
    const key = groupKeyOf(result);
    if (!byKey.has(key)) {
      byKey.set(key, []);
      keyOrder.push(key);
    }
    byKey.get(key)!.push({ index, result });
  });

  const groups: ResultGroup[] = [];
  for (const key of keyOrder) {
    const items = byKey.get(key)!;
    const docs = items.filter(it => it.result.type === DOCS_TYPE);
    const entities = items.filter(it => it.result.type !== DOCS_TYPE);
    if (docs.length === 0) {
      // Entity (or non-doc) with no docs in the results → standalone row(s), as before.
      for (const it of entities) groups.push({ kind: 'single', index: it.index, result: it.result });
      continue;
    }
    // The DOC row (tier 1) is the doc's root/landing page — and only that. When no root matched, the
    // finding has no DOC row and every hit stays tier-2 content under the header, keeping its own
    // title and its page/section badge.
    const docRow = docs.find(it => isDocRoot(it.result));
    const content = docs.filter(it => it !== docRow);
    // Tier 1 = the ENTITY row(s) then the DOC row. Header = entity display name, else the doc's name.
    const primary = docRow ? [...entities, docRow] : entities;
    const name = entities[0]?.result.document.title || docNameOf(docs[0].result);
    groups.push({ kind: 'group', name, primary, content });
  }
  return groups;
}

/**
 * Limit results by FINDING, not by flat row. Groups first, keeps the top `maxFindings` findings, and
 * within each caps the TIER-2 content rows to `maxDocsPerFinding` (whatever tier 1 holds — the entity,
 * the doc row, or neither — is ALWAYS kept, since a finding may have no doc-root row at all).
 * Returns a flat list (tier 1, then content) that re-groups into the same findings — so
 * ONE finding with many pages can't fill the window and hide every other finding (the "apple only shows
 * Apple-App-Store" bug). Row-slicing the flat list before grouping is what caused that.
 */
export function limitFindings(
  results: SearchResult[],
  opts: { maxFindings?: number; maxDocsPerFinding?: number } = {},
): SearchResult[] {
  const { maxFindings = 6, maxDocsPerFinding = 3 } = opts;
  const out: SearchResult[] = [];
  for (const g of buildGroups(results).slice(0, maxFindings)) {
    if (g.kind === 'single') {
      out.push(g.result);
      continue;
    }
    // tier 1 (entity and/or doc row) kept whole; content (pages/sections) capped.
    out.push(
      ...g.primary.map(it => it.result),
      ...g.content.slice(0, maxDocsPerFinding).map(it => it.result),
    );
  }
  return out;
}

/** Flat list of result indices in RENDER (grouped) order — for arrow-key nav that follows the eye. */
export function visualOrder(groups: ResultGroup[]): number[] {
  const order: number[] = [];
  for (const g of groups) {
    if (g.kind === 'single') {
      order.push(g.index);
    } else {
      for (const it of g.primary) order.push(it.index);
      for (const it of g.content) order.push(it.index);
    }
  }
  return order;
}

export function SearchResultGroups({
  results,
  activeIndex = -1,
  onSelect,
  onHover,
}: {
  results: SearchResult[];
  /** Index of the highlighted row (React-controlled highlight). Omit to let a DOM effect own it. */
  activeIndex?: number;
  onSelect: (result: SearchResult) => void;
  onHover?: (index: number) => void;
}) {
  const { t } = useTranslationRef(searchMessages);
  const groups = useMemo(() => buildGroups(results), [results]);

  const kindLabels: Record<ResultKind, string> = {
    entity: t('resultKind.entity'),
    doc: t('resultKind.doc'),
    page: t('resultKind.page'),
    section: t('resultKind.section'),
  };

  // `tier` sets the indent (0 = flush single row, 1 = first indent, 2 = deeper). `docTitle` is passed
  // ONLY for the tier-1 doc row (a verified doc-root result) — see the call site.
  const row = (index: number, result: SearchResult, tier: 0 | 1 | 2, docTitle?: string) => {
    const { document } = result;
    // A doc-root row is badged DOC and labelled with the document's NAME, because its landing page's
    // own title is usually just "Overview"/"Home". Every other row keeps its real kind and title, so
    // a section reads as a section and lands on the anchor it names.
    const kind = docTitle ? 'doc' : classifyResult(result);
    const kindLabel = kindLabels[kind];
    const title = docTitle || document.title;
    const tierClassByTier: Record<number, string> = {
      1: ' devhub-sr__item--tier1',
      2: ' devhub-sr__item--tier2',
    };
    const tierClass = tierClassByTier[tier] ?? '';
    return (
      <button
        key={`${document.location}-${index}`}
        type="button"
        className={`devhub-sr__item${tierClass}${index === activeIndex ? ' is-active' : ''}`}
        data-kind={kind}
        data-index={index}
        data-location={document.location}
        role="option"
        aria-selected={index === activeIndex}
        onMouseEnter={() => onHover?.(index)}
        onClick={() => onSelect(result)}
      >
        <odx-icon className="devhub-sr__item-icon" name={KIND_ICON[kind]} size="sm" />
        <span className="devhub-sr__item-text">
          <span className="devhub-sr__item-title">{title}</span>
          <ResultSnippet result={result} />
        </span>
        <span className={`devhub-sr__item-kind devhub-sr__item-kind--${kind}`}>{kindLabel}</span>
        <odx-icon className="devhub-sr__item-chevron" name="core::chevron-right" size="sm" />
      </button>
    );
  };

  return (
    <ul className="devhub-sr__list">
      {groups.map(group => {
        if (group.kind === 'single') {
          return <li key={`s-${group.index}`}>{row(group.index, group.result, 0)}</li>;
        }
        return (
          <li
            key={`g-${group.name}-${(group.primary[0] ?? group.content[0])?.index}`}
            className="devhub-sr__group"
          >
            {/* TIER 0 — plain finding title (not a clickable result row). */}
            <div className="devhub-sr__group-title" aria-hidden="true">
              {group.name || t('resultKind.page')}
            </div>
            {/* TIER 1 — the catalog entity and the doc, as siblings. buildGroups only ever puts the
                doc's ROOT page here, so labelling it with the doc name is truthful; the entity keeps
                its own title and ENTITY badge. */}
            {group.primary.map(({ index, result }) =>
              result.type === DOCS_TYPE
                ? row(index, result, 1, group.name)
                : row(index, result, 1),
            )}
            {/* TIER 2 — the doc's content (pages / section hits). */}
            {group.content.map(({ index, result }) => row(index, result, 2))}
          </li>
        );
      })}
    </ul>
  );
}
